import React, {
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Portal } from "@headlessui/react";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";

import { ReactComponent as CheckmarkIcon } from "../../assets/icons/checkmark-new.svg";
import { ReactComponent as ChevronDownIcon } from "../../assets/icons/chevron-down.svg";
import { ReactComponent as ChevronSelectorIcon } from "../../assets/icons/chevron-selector-vertical.svg";
import { ReactComponent as SearchIcon } from "../../assets/icons/search-sm.svg";
import { ReactComponent as XClose } from "../../assets/icons/x-close.svg";
import useMediaQuery from "../../hooks/useMediaQuery";

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
  /** Leading preview drawn in the list and, once picked, in the trigger — never in the native <option>. */
  icon?: React.ReactNode;
  /** Second line under the label in the list; also matched by search. */
  description?: string;
  /** Heading the option sits under; consecutive options sharing one are grouped. */
  group?: string;
}

/** Label + message frame shared by Select and any hand-built input, so the two read identically. */
export interface FieldProps {
  label?: string;
  /** Message under the field. Error wins over hint, and paints the danger tone. */
  error?: string;
  hint?: string;
  /** Non-error message with its own tone — a caution or an aside about the current choice. */
  note?: string;
  noteTone?: "warning" | "info";
  htmlFor?: string;
  className?: string;
  children: React.ReactNode;
}

const NOTE_TONE: Record<NonNullable<FieldProps["noteTone"]>, string> = {
  warning: "text-eai-warning-text",
  info: "text-eai-info-text",
};

export const Field: React.FC<FieldProps> = ({
  label, error, hint, note, noteTone = "warning", htmlFor, className = "", children,
}) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    {label && (
      <label htmlFor={htmlFor} className="eai-meta-label block">
        {label}
      </label>
    )}
    {children}
    {(error || hint) && (
      <p className={`text-eai-body-xs ${error ? "text-eai-danger" : "text-eai-ink-subtle"}`}>
        {error || hint}
      </p>
    )}
    {!error && note && <p className={`text-eai-body-xs ${NOTE_TONE[noteTone]}`}>{note}</p>}
  </div>
);

/** The field chevron for a custom `trigger`: a down arrow on desktop, up-down chevrons on a phone where the list opens as a centered card. */
export const SelectChevron: React.FC<{ className?: string }> = ({ className = "" }) => {
  const isPhone = useMediaQuery("(max-width: 639px)");
  const Icon = isPhone ? ChevronSelectorIcon : ChevronDownIcon;
  return <Icon aria-hidden="true" className={`shrink-0 [&_path]:stroke-current ${className}`} />;
};

// `size` is repurposed as a density variant — the native numeric attribute only styles list-boxes, and every field here is a single-value picker.
export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  /** Options as data. Omit and pass <option> children instead when they need custom markup. */
  options?: SelectOption[];
  /** Renders a eai-meta-label above the field, wired to the trigger by id. */
  label?: string;
  /** Leading empty option — "" is the app's convention for unset, so it stays selectable. */
  placeholder?: string;
  /** "none" hands text and padding back to the caller — for the icon-only responsive toolbars. */
  size?: "sm" | "md" | "none";
  /** Message under the field; also turns the border and focus ring red. */
  error?: string;
  /** Message under the field when there's no error. */
  hint?: string;
  /** Leading adornment, e.g. a filter icon. Adds the left padding it needs. */
  icon?: React.ReactNode;
  /** Off for a field that draws its own chevron or is too narrow to hold one. */
  chevron?: boolean;
  chevronClassName?: string;
  containerClassName?: string;
  labelClassName?: string;
  /** Search box above the list, filtering on label, description and group. */
  searchable?: boolean;
  /** Custom trigger face, e.g. a toolbar pill: the button keeps only `className` and shows this instead of the value. */
  trigger?: React.ReactNode;
  /** Multi-select only: lets every row be dragged by a grip on the row into a new order. Called with all the option values in that order; the caller owns the order and passes the options back in it. */
  onReorder?: (values: string[]) => void;
  /** Desktop list's minimum width in px, for a list whose rows carry more than the trigger can show (it still never leaves the viewport). Defaults to 176. */
  listMinWidth?: number;
}

// A row that can be dragged — by its grip only, so the rest of the row keeps its click-to-toggle. Only the vertical movement is applied, so the row stays in its column. Hands the row what it needs to wear: a ref, a style and the grip to place.
const SortableOption: React.FC<{
  id: string;
  children: (parts: { ref: (node: HTMLElement | null) => void; style: React.CSSProperties; grip: React.ReactNode }) => React.ReactNode;
}> = ({ id, children }) => {
  const { setNodeRef, listeners, transform, transition, isDragging } = useSortable({ id });
  return (
    <>
      {children({
        ref: setNodeRef,
        style: {
          transform: transform ? `translate3d(0, ${Math.round(transform.y)}px, 0)` : undefined,
          transition,
          position: "relative",
          zIndex: isDragging ? 1 : undefined,
          opacity: isDragging ? 0.85 : undefined,
        },
        grip: (
          <span
            {...listeners}
            role="button"
            aria-label="Drag to reorder"
            title="Drag to reorder"
            // A tap on the grip, or the click that ends a drag, must not tick or clear the row
            onClick={(e) => e.stopPropagation()}
            className="-mr-1 ml-auto flex h-6 w-6 shrink-0 cursor-grab select-none items-center justify-center rounded text-eai-ink-subtle hover:bg-eai-border active:cursor-grabbing"
            style={{ touchAction: "none" }}
          >
            <span aria-hidden="true" className="text-base leading-none tracking-[-0.15em]">⋮⋮</span>
          </span>
        ),
      })}
    </>
  );
};

const SIZE_CLASSES: Record<NonNullable<SelectProps["size"]>, string> = {
  sm: "py-1.5 text-eai-body-xs",
  md: "py-2 text-eai-body-sm",
  none: "",
};

// Trigger heights come off the control ladder, so a field is never a couple of pixels shy of the button beside it. Applied to the single-value trigger only — a native `multiple` select must keep its natural multi-row height.
const TRIGGER_HEIGHT: Record<NonNullable<SelectProps["size"]>, string> = {
  sm: "h-eai-control-sm",
  md: "h-eai-control-lg",
  none: "",
};

// The list mirrors the trigger's size — a picked value must not resize as it lands in the field
const OPTION_SIZE_CLASSES: Record<NonNullable<SelectProps["size"]>, string> = {
  sm: "text-eai-body-xs",
  md: "text-eai-body-sm",
  none: "text-eai-body-sm",
};

// Mirrors .eai-select-list's max-h-60 (and the searchable list's max-h-80) for the flip maths only; the gap is the trigger-to-list space, the edge keeps the list off the viewport border
const LIST_MAX_HEIGHT = 240;
const SEARCHABLE_LIST_MAX_HEIGHT = 320;
const LIST_GAP = 4;
const LIST_EDGE = 8;

interface Item {
  value: string;
  label: React.ReactNode;
  text: string;
  disabled: boolean;
  group?: string;
  icon?: React.ReactNode;
  description?: string;
}

// Plain text of a node, for the trigger's title and for keyboard type-ahead.
const textOf = (node: React.ReactNode): string => {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (React.isValidElement(node)) return textOf(node.props.children);
  return "";
};

// Call sites hand us <option> children — often inside fragments, maps and conditionals — so the list is read back out of the element tree rather than duplicated as a prop.
const collectItems = (children: React.ReactNode, group?: string, out: Item[] = []): Item[] => {
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === React.Fragment) {
      collectItems(child.props.children, group, out);
      return;
    }
    if (child.type === "optgroup") {
      collectItems(child.props.children, child.props.label, out);
      return;
    }
    if (child.type !== "option") return;
    const text = textOf(child.props.children);
    out.push({
      value: child.props.value !== undefined ? String(child.props.value) : text,
      label: child.props.children,
      text,
      disabled: Boolean(child.props.disabled),
      group,
    });
  });
  return out;
};

const buildItems = (
  options: SelectOption[] | undefined,
  placeholder: string | undefined,
  multiple: boolean | undefined,
  children: React.ReactNode
): Item[] => {
  const fromOptions: Item[] = (options ?? []).map((o) => ({
    value: String(o.value),
    label: o.label,
    text: o.label,
    disabled: Boolean(o.disabled),
    icon: o.icon,
    description: o.description,
    group: o.group,
  }));
  // A multi-select's placeholder is only the empty-trigger text, never a row to tick
  const head: Item[] = placeholder && !multiple
    ? [{ value: "", label: placeholder, text: placeholder, disabled: false }]
    : [];
  const all = [...head, ...fromOptions, ...collectItems(children)];
  // Gather each group's options together, groups in first-seen order, so a heading never repeats
  if (!all.some((i) => i.group)) return all;
  const order = Array.from(new Set(all.map((i) => i.group)));
  return order.flatMap((g) => all.filter((i) => i.group === g));
};

// Search match on label, description and group; q is already trimmed and lowercased
const matchesQuery = (item: Item, q: string) =>
  [item.text, item.description, item.group].some((t) => t?.toLowerCase().includes(q));

// React tracks the select's value internally, so assigning through the prototype setter and dispatching a real change event is what makes both React and react-hook-form pick it up.
const setNativeValue = (node: HTMLSelectElement, value: string) => {
  const setter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, "value")?.set;
  setter ? setter.call(node, value) : (node.value = value);
  node.dispatchEvent(new Event("change", { bubbles: true }));
};

/** The app's one dropdown. A real <select> stays in the DOM — hidden — so form submission, refs and react-hook-form's register() keep working, while the trigger and the option list are ours to style. Props and onChange match the native element, so it drops straight into any <select> call site; with `multiple`, read e.target.selectedOptions as usual. */
const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      options,
      label,
      placeholder,
      size = "md",
      error,
      hint,
      icon,
      chevron = true,
      chevronClassName = "",
      containerClassName = "",
      labelClassName = "",
      searchable,
      trigger,
      onReorder,
      listMinWidth = 176,
      className = "",
      children,
      id,
      multiple,
      disabled,
      value,
      defaultValue,
      onChange,
      onBlur,
      required,
      name,
      title,
      ...rest
    },
    forwardedRef
  ) => {
    const nativeRef = useRef<HTMLSelectElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(forwardedRef, () => nativeRef.current as HTMLSelectElement);

    const generatedId = useId();
    const selectId = id ?? generatedId;
    const listId = `${selectId}-listbox`;
    const describedById = error || hint ? `${selectId}-message` : undefined;

    // max-width, not !min-width, so jsdom (no matchMedia) keeps the desktop list
    const isPhone = useMediaQuery("(max-width: 639px)");
    const [open, setOpen] = useState(false);
    const [sheetTitle, setSheetTitle] = useState("");
    const [query, setQuery] = useState("");
    const searchRef = useRef<HTMLInputElement>(null);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [rect, setRect] = useState<{ top: number; left: number; width: number; drop: boolean; maxHeight?: number }>({
      top: 0,
      left: 0,
      width: 0,
      drop: true,
    });

    // Rebuilt every render: <option> children are new elements each time, so a memo keyed on them would never hold
    const items = buildItems(options, placeholder, multiple, children);
    // Changes only when the option values do — what the readback below needs to rerun on
    const itemsKey = items.map((i) => i.value).join("\u0000");

    const controlled = value !== undefined && value !== null;
    const [currentValue, setCurrentValue] = useState(() =>
      controlled ? String(value) : defaultValue !== undefined ? String(defaultValue) : ""
    );
    // A multi-select's picks; array value/defaultValue, as on the native element
    const toList = (v: unknown) => (Array.isArray(v) ? v.map(String) : v === undefined || v === null || v === "" ? [] : [String(v)]);
    const [currentValues, setCurrentValues] = useState<string[]>(() => toList(controlled ? value : defaultValue));

    useEffect(() => {
      if (!controlled) return;
      multiple ? setCurrentValues(toList(value)) : setCurrentValue(String(value));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controlled, value, multiple]);

    // Uncontrolled fields let the browser pick the first option — read it back so the trigger shows what the hidden select actually holds
    useEffect(() => {
      const node = nativeRef.current;
      if (!controlled && !multiple && node && node.value !== currentValue) setCurrentValue(node.value);
    }, [controlled, multiple, currentValue, itemsKey]);

    const isPicked = (v: string) => (multiple ? currentValues.includes(v) : v === currentValue);
    const selected = multiple ? undefined : items.find((i) => i.value === currentValue);
    const pickedItems = multiple ? items.filter((i) => currentValues.includes(i.value)) : [];
    const selectedLabel = multiple
      ? pickedItems.length === 1
        ? pickedItems[0].label
        : pickedItems.length > 1
          ? `${pickedItems.length} selected`
          : placeholder ?? ""
      : selected
        ? selected.label
        : placeholder ?? "";
    const hasValue = multiple ? pickedItems.length > 0 : Boolean(selected);
    const triggerIcon = multiple ? (pickedItems.length === 1 ? pickedItems[0].icon : undefined) : selected?.icon;

    // What the open list shows; the query is cleared on close, so a closed list's indexes are the full list's
    const trimmedQuery = query.trim().toLowerCase();
    const visible = trimmedQuery ? items.filter((i) => matchesQuery(i, trimmedQuery)) : items;

    const position = useCallback(() => {
      const node = triggerRef.current;
      if (!node) return;
      const r = node.getBoundingClientRect();
      const cap = searchable ? SEARCHABLE_LIST_MAX_HEIGHT : LIST_MAX_HEIGHT;
      // Size the flip to the real list (capped like the CSS), so a short list stays under the trigger and a long one opens toward the roomier side
      const wanted = Math.min(listRef.current?.scrollHeight ?? cap, cap);
      const below = window.innerHeight - r.bottom - LIST_GAP - LIST_EDGE;
      const above = r.top - LIST_GAP - LIST_EDGE;
      const drop = below >= wanted || below >= above;
      // Only when the chosen side is short of the CSS cap does the list shrink and scroll; otherwise the CSS max-height stays in charge
      const room = Math.max(drop ? below : above, 0);
      const maxHeight = room < cap ? room : undefined;
      // At least 11rem wide (or the caller's listMinWidth), so an icon-only trigger still gets a readable list, and kept inside the viewport
      const width = Math.min(Math.max(r.width, listMinWidth), window.innerWidth - LIST_EDGE * 2);
      const left = Math.max(LIST_EDGE, Math.min(r.left, window.innerWidth - width - LIST_EDGE));
      setRect({ top: drop ? r.bottom + LIST_GAP : r.top - LIST_GAP, left, width, drop, maxHeight });
    }, [searchable, listMinWidth]);

    useLayoutEffect(() => {
      if (!open) return;
      position();
      // Capture phase so a scroll in any ancestor keeps the list pinned to its trigger.
      // The list's own scrolling must not re-measure — only a scroll elsewhere moves the trigger
      const onScroll = (e: Event) => {
        if (e.target instanceof Node && listRef.current?.contains(e.target)) return;
        position();
      };
      window.addEventListener("scroll", onScroll, true);
      window.addEventListener("resize", onScroll);
      return () => {
        window.removeEventListener("scroll", onScroll, true);
        window.removeEventListener("resize", onScroll);
      };
      // items.length: options that land after the list opens (a lazy fetch) re-pick the side
    }, [open, position, items.length]);

    // Drop the highlight on close, so reopening lands on the current value rather than wherever the arrow keys were left last time.
    useEffect(() => {
      if (!open) {
        setActiveIndex(-1);
        setQuery("");
      }
    }, [open]);

    // Freeze the page behind the phone card; a Dialog already does this, so restore whatever was there
    useEffect(() => {
      if (!open || !isPhone) return;
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }, [open, isPhone]);

    // Focus the phone card's listbox the moment its portal mounts (a stable callback ref, so a re-render can't steal focus back from the search box); the list, not the search input, so no keyboard pops up
    const focusPhoneList = useCallback((node: HTMLDivElement | null) => node?.focus({ preventScroll: true }), []);

    const closeToTrigger = () => {
      setOpen(false);
      triggerRef.current?.focus();
    };

    // Escape closes only the picker — stopped here so the Dialog behind it keeps its own Escape for the next press
    const onCardKeyDown = (e: React.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      closeToTrigger();
    };

    // The phone list drives itself from the keyboard like the trigger does; Escape is left to bubble to the card, and Tab to move on to Close/Done
    const onPhoneListKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Tab") return;
      onKeyDown(e);
    };

    // Desktop lands in the search box; a phone waits for a tap, so the keyboard doesn't cover the list
    useEffect(() => {
      if (open && searchable && !isPhone) searchRef.current?.focus();
    }, [open, searchable, isPhone]);

    useEffect(() => {
      if (!open) return;
      const onPointer = (e: MouseEvent) => {
        const t = e.target as Node;
        if (!listRef.current?.contains(t) && !triggerRef.current?.contains(t)) setOpen(false);
      };
      document.addEventListener("mousedown", onPointer);
      return () => document.removeEventListener("mousedown", onPointer);
    }, [open]);

    // Keep the active row in view while arrowing through a long list.
    useEffect(() => {
      if (!open || activeIndex < 0) return;
      listRef.current
        ?.querySelector(`[data-index="${activeIndex}"]`)
        ?.scrollIntoView({ block: "nearest" });
    }, [open, activeIndex]);

    // The grip is an explicit handle, so a few pixels of movement starts the drag
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
    const handleDragEnd = ({ active, over }: DragEndEvent) => {
      if (!over || active.id === over.id) return;
      const all = items.map((i) => i.value);
      const from = all.indexOf(String(active.id));
      const to = all.indexOf(String(over.id));
      if (from < 0 || to < 0) return;
      onReorder?.(arrayMove(all, from, to));
    };

    const commit = (next: string) => {
      const node = nativeRef.current;
      // Multi-select toggles one option and stays open; the change event carries the full selectedOptions
      if (multiple) {
        const nextValues = currentValues.includes(next) ? currentValues.filter((v) => v !== next) : [...currentValues, next];
        if (node) {
          Array.from(node.options).forEach((o) => (o.selected = nextValues.includes(o.value)));
          node.dispatchEvent(new Event("change", { bubbles: true }));
        }
        if (!controlled) setCurrentValues(nextValues);
        return;
      }
      if (node) setNativeValue(node, next);
      if (!controlled) setCurrentValue(next);
      setOpen(false);
      triggerRef.current?.focus();
    };

    // Sheet heading: own label, aria-label, a <label for>, else the label a <Field> renders just before us
    const findTitle = () => {
      if (label) return label;
      if (rest["aria-label"]) return rest["aria-label"];
      const forLabel = document.querySelector(`label[for="${CSS.escape(selectId)}"]`);
      if (forLabel?.textContent) return forLabel.textContent;
      const prev = triggerRef.current?.closest("div")?.parentElement?.previousElementSibling;
      return prev?.tagName === "LABEL" ? prev.textContent ?? "" : "";
    };

    const openAt = (index: number) => {
      setActiveIndex(index);
      if (isPhone) setSheetTitle(findTitle());
      setOpen(true);
    };

    const step = (from: number, dir: 1 | -1) => {
      for (let i = from + dir; i >= 0 && i < visible.length; i += dir) {
        if (!visible[i].disabled) return i;
      }
      return from;
    };

    const typeahead = useRef({ buffer: "", at: 0 });
    const onType = (key: string) => {
      const now = Date.now();
      const t = typeahead.current;
      t.buffer = now - t.at > 500 ? key : t.buffer + key;
      t.at = now;
      const match = items.findIndex(
        (i) => !i.disabled && i.text.toLowerCase().startsWith(t.buffer.toLowerCase())
      );
      if (match < 0) return;
      open || multiple ? openAt(match) : commit(items[match].value);
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
      const selectedIndex = items.findIndex((i) => isPicked(i.value));
      const index = open && activeIndex >= 0 ? activeIndex : selectedIndex;
      switch (e.key) {
        case "ArrowDown":
        case "ArrowUp": {
          e.preventDefault();
          const dir = e.key === "ArrowDown" ? 1 : -1;
          open ? setActiveIndex(step(index, dir)) : openAt(index < 0 ? 0 : index);
          return;
        }
        case "Home":
        case "End":
          if (!open) return;
          e.preventDefault();
          setActiveIndex(e.key === "Home" ? step(-1, 1) : step(visible.length, -1));
          return;
        case "Enter":
        case " ":
          e.preventDefault();
          if (!open) openAt(index < 0 ? 0 : index);
          else if (index >= 0 && visible[index] && !visible[index].disabled) commit(visible[index].value);
          return;
        case "Escape":
          if (open) {
            e.preventDefault();
            setOpen(false);
          }
          return;
        case "Tab":
          setOpen(false);
          return;
        default:
          if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
            e.preventDefault();
            onType(e.key);
          }
      }
    };

    // The search box types freely; only list navigation is handed to the trigger's handler
    const onSearchKeyDown = (e: React.KeyboardEvent) => {
      if (["ArrowDown", "ArrowUp", "Enter", "Escape", "Tab"].includes(e.key)) onKeyDown(e);
      if (e.key === "Escape" || e.key === "Tab") triggerRef.current?.focus();
    };

    const onQuery = (next: string) => {
      setQuery(next);
      const q = next.trim().toLowerCase();
      // Highlight the first match, so Enter picks it
      const first = q
        ? items.filter((i) => matchesQuery(i, q)).findIndex((i) => !i.disabled)
        : -1;
      setActiveIndex(first);
    };

    // Row body shared by the desktop list and the phone card: a checkbox for multi on desktop (the phone card ticks a row with a checkmark instead — tap to select, tap again to clear), then the option's icon, then its label
    const rowBody = (item: Item, wrap: boolean) => (
      <>
        {multiple && !wrap && (
          <input
            type="checkbox"
            readOnly
            tabIndex={-1}
            checked={isPicked(item.value)}
            className="pointer-events-none h-4 w-4 shrink-0 rounded border-eai-border-hover accent-eai-cta-primary"
          />
        )}
        {item.icon && <span className="flex shrink-0 items-center">{item.icon}</span>}
        {item.description ? (
          <span className="min-w-0 grow">
            <span className={`block ${wrap ? "" : "truncate"}`}>{item.label}</span>
            <span className="block whitespace-normal text-eai-body-xs text-eai-ink-subtle">{item.description}</span>
          </span>
        ) : (
          <span className={`min-w-0 grow ${wrap ? "" : "truncate"}`}>{item.label}</span>
        )}
      </>
    );

    const searchBox = searchable && (
      <div className="border-b border-eai-border p-2">
        <div className="relative">
          <SearchIcon aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-eai-ink-faint [&_path]:stroke-current" />
          <input
            ref={searchRef}
            type="text"
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            onKeyDown={onSearchKeyDown}
            placeholder="Search…"
            aria-label="Search options"
            aria-controls={listId}
            aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
            className="eai-input pl-9"
          />
        </div>
      </div>
    );

    // Option rows with a heading wherever the group changes; `phone` swaps in the card's larger, wrapping rows
    const sortable = Boolean(multiple && onReorder && !trimmedQuery);
    const renderRow = (item: Item, index: number, picked: boolean, phone: boolean, rich: boolean, ref?: any, style?: React.CSSProperties, grip?: React.ReactNode) => (
      <div
        ref={ref}
        style={style}
        id={`${listId}-${index}`}
        data-index={index}
        role="option"
        aria-selected={picked}
        aria-disabled={item.disabled || undefined}
        onMouseEnter={phone ? undefined : () => !item.disabled && setActiveIndex(index)}
        onClick={() => !item.disabled && commit(item.value)}
        className={
          phone
            // The arrow-key highlight shows only while the list has keyboard focus, so a tap-opened card isn't pre-highlighted
            ? `eai-select-option mx-2 flex items-center gap-3 whitespace-normal py-3 text-eai-body-sm ${index === activeIndex ? "group-focus-visible:bg-eai-surface group-focus-visible:text-eai-ink" : ""} ${picked ? "eai-select-option-selected" : ""} ${item.disabled ? "eai-select-option-disabled" : ""}`
            : `eai-select-option ${OPTION_SIZE_CLASSES[size]} ${index === activeIndex ? "eai-select-option-highlighted" : ""} ${picked ? "eai-select-option-selected" : ""} ${item.disabled ? "eai-select-option-disabled" : ""} ${rich ? "flex items-center gap-2" : ""}`
        }
      >
        {phone || rich ? rowBody(item, phone) : item.label}
        {phone && picked && (
          <CheckmarkIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-eai-cta-primary [&_polyline]:stroke-current" />
        )}
        {grip}
      </div>
    );

    const rowsInner = (phone: boolean) =>
      visible.length === 0 ? (
        <div className="px-3 py-4 text-center text-eai-body-sm text-eai-ink-subtle">No matches for "{query}"</div>
      ) : (
        visible.map((item, index) => {
          const picked = isPicked(item.value);
          const heading = item.group && item.group !== visible[index - 1]?.group;
          const rich = Boolean(multiple || item.icon || item.description);
          return (
            <React.Fragment key={`${item.value}-${index}`}>
              {heading && <div role="presentation" className={`eai-meta-label pb-1 pt-2 ${phone ? "px-5" : "px-4"}`}>{item.group}</div>}
              {sortable && !item.disabled ? (
                <SortableOption id={item.value}>{({ ref, style, grip }) => renderRow(item, index, picked, phone, rich, ref, style, grip)}</SortableOption>
              ) : (
                renderRow(item, index, picked, phone, rich)
              )}
            </React.Fragment>
          );
        })
      );

    const rows = (phone: boolean) =>
      sortable ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={items.filter((i) => !i.disabled).map((i) => i.value)}
            strategy={verticalListSortingStrategy}
          >
            {rowsInner(phone)}
          </SortableContext>
        </DndContext>
      ) : (
        rowsInner(phone)
      );

    const frame = trigger
      ? className
      : `eai-select ${SIZE_CLASSES[size]} ${icon ? "pl-10" : ""} ${
          error ? "border-eai-danger focus:border-eai-danger focus:ring-eai-danger" : ""
        } ${className}`;
    const showChevron = chevron && !trigger;

    const nativeSelect = (
      <select
        {...rest}
        ref={nativeRef}
        name={name}
        required={required}
        disabled={disabled}
        multiple={multiple}
        onChange={onChange}
        onBlur={onBlur}
        {...(controlled ? { value } : { defaultValue })}
        // Kept in the DOM as the value holder: it still submits, still takes a ref, and react-hook-form still reads it. aria-hidden because the trigger carries the ARIA.
        aria-hidden
        tabIndex={-1}
        className="sr-only"
        id={`${selectId}-native`}
      >
        {placeholder && !multiple && <option value="">{placeholder}</option>}
        {options?.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
        {children}
      </select>
    );

    return (
      <div className={containerClassName}>
        {label && (
          <label htmlFor={selectId} className={`eai-meta-label mb-1.5 block ${labelClassName}`}>
            {label}
          </label>
        )}

        {/* Only anchor when there is something of ours to position — the list is portalled and fixed. A stray `relative` would outrank a call site's own absolutely-placed icons, which is exactly how the icon-only toolbars draw theirs. */}
        <div className={showChevron || icon ? "relative" : ""}>
          {nativeSelect}

          {icon && (
            <span className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center pl-3 text-eai-ink-faint">
              {icon}
            </span>
          )}

          <button
            {...(rest["aria-label"] ? { "aria-label": rest["aria-label"] } : {})}
            ref={triggerRef}
            type="button"
            id={selectId}
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={open ? listId : undefined}
            aria-activedescendant={open && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedById}
            aria-required={required}
            disabled={disabled}
            title={title ?? (typeof selectedLabel === "string" ? selectedLabel : undefined)}
            onKeyDown={onKeyDown}
            onBlur={onBlur as unknown as React.FocusEventHandler<HTMLButtonElement>}
            onClick={() =>
              open
                ? setOpen(false)
                : openAt(items.findIndex((i) => isPicked(i.value)))
            }
            className={
              trigger
                ? frame
                : `${frame} ${TRIGGER_HEIGHT[size]} flex items-center text-left ${open ? "border-eai-cta-primary ring-1 ring-eai-cta-primary" : ""}`
            }
          >
            {trigger ?? (
              <>
                {triggerIcon && <span className="mr-2 flex shrink-0 items-center">{triggerIcon}</span>}
                <span className={`block truncate ${hasValue ? "" : "text-eai-ink-disabled"}`}>
                  {selectedLabel}
                </span>
              </>
            )}
          </button>

          {/* Up-down chevrons on a phone: the field opens a centered picker, not a list that drops below it */}
          {showChevron && isPhone && (
            <ChevronSelectorIcon
              aria-hidden="true"
              className={`pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-eai-ink-faint [&_path]:stroke-current ${chevronClassName}`}
            />
          )}
          {showChevron && !isPhone && (
            <ChevronDownIcon
              aria-hidden="true"
              className={`pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-eai-ink-faint transition-transform [&_path]:stroke-current ${
                open ? "rotate-180" : ""
              } ${chevronClassName}`}
            />
          )}
        </div>

        {open && isPhone && (
          // Headless UI's Portal, not createPortal: inside a Dialog it registers as a nested portal, so iOS scroll-lock lets the list scroll and a tap here isn't an outside click
          <Portal>
            <div ref={listRef} className="fixed inset-0 z-[10056] flex items-center justify-center p-4">
              <div aria-hidden="true" className="absolute inset-0 bg-black/30" onClick={closeToTrigger} />
              {/* Centered, not a bottom sheet: on iPhone the sheet's foot read as cut off; 70dvh fits the toolbar-shrunk viewport, 70vh where dvh is unsupported */}
              <div
                onKeyDown={onCardKeyDown}
                role="dialog"
                aria-modal="true"
                aria-labelledby={`${selectId}-sheet-title`}
                className="relative flex max-h-[70vh] supports-[height:100dvh]:max-h-[70dvh] w-full max-w-sm animate-slideUp flex-col overflow-hidden rounded-eai-card bg-eai-surface-control shadow-eai-card focus:outline-none"
              >
                <div className="flex items-center gap-3 border-b border-eai-border py-3 pl-5 pr-3">
                  <div id={`${selectId}-sheet-title`} className="eai-h3-title min-w-0 grow truncate">{sheetTitle || "Select an option"}</div>
                  <button
                    type="button"
                    onClick={closeToTrigger}
                    aria-label="Close"
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-eai-ink-subtle transition-colors active:bg-eai-surface-active"
                  >
                    <XClose className="h-5 w-5 [&_path]:stroke-current" />
                  </button>
                </div>
                {searchBox}
                <div
                  ref={focusPhoneList}
                  tabIndex={-1}
                  onKeyDown={onPhoneListKeyDown}
                  id={listId}
                  role="listbox"
                  aria-labelledby={selectId}
                  aria-multiselectable={multiple || undefined}
                  aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
                  className="group space-y-1 overflow-y-auto overscroll-contain py-2 focus:outline-none"
                >
                  {rows(true)}
                </div>
                {multiple && (
                  <div className="flex justify-end border-t border-eai-border p-3">
                    <button type="button" onClick={closeToTrigger} className="eai-btn-cta">
                      Done
                    </button>
                  </div>
                )}
              </div>
            </div>
          </Portal>
        )}

        {open && !isPhone && (
          // Portalled and fixed, so a modal's overflow can't clip it; Headless UI's Portal so the Dialog's focus lock lets the search box take focus
          <Portal>
            <div
              ref={listRef}
              style={{
                position: "fixed",
                top: rect.drop ? rect.top : undefined,
                bottom: rect.drop ? undefined : window.innerHeight - rect.top,
                left: rect.left,
                width: rect.width,
                maxHeight: rect.maxHeight,
              }}
              className={`eai-select-list ${searchable ? "flex max-h-80 flex-col space-y-0 overflow-hidden py-0" : ""}`}
              {...(searchable ? {} : { id: listId, role: "listbox", "aria-labelledby": selectId, "aria-multiselectable": multiple || undefined })}
            >
              {searchable ? (
                <>
                  {searchBox}
                  <div id={listId} role="listbox" aria-labelledby={selectId} aria-multiselectable={multiple || undefined} className="space-y-1 overflow-y-auto py-1">
                    {rows(false)}
                  </div>
                </>
              ) : (
                rows(false)
              )}
            </div>
          </Portal>
        )}

        {(error || hint) && (
          <p id={describedById} className={`mt-1 text-eai-body-xs ${error ? "text-eai-danger" : "text-eai-ink-subtle"}`}>
            {error || hint}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = "Select";

export default Select;
